/**
 * PushNotificationBanner — Opt-in UI for Web Push notifications.
 *
 * Shows a dismissible banner prompting the user to enable push notifications.
 * Respects user preference via localStorage (dismissed = don't show again).
 */

import { useState, useEffect, useCallback } from "react";
import { useClientContext } from "../ClientContext.jsx";
import {
  isPushSupported,
  getPushPermissionState,
  hasActiveSubscription,
  subscribeToPush,
} from "../services/pushService.js";
import { RADIUS, SP, TRANSITION, type ThemeObject } from "../shared/theme.js";

const LS_KEY = "lpa_push_dismissed";

export default function PushNotificationBanner() {
  const { T, clientId } = useClientContext();
  const [visible, setVisible] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    // Don't show if:
    // - Already dismissed
    // - Push not supported
    // - Permission already granted or denied
    // - Already has an active subscription
    const dismissed = localStorage.getItem(LS_KEY) === "1";
    if (dismissed || !isPushSupported()) return;

    const state = getPushPermissionState();
    if (state === "denied" || state === "unsupported") return;

    hasActiveSubscription().then((active) => {
      if (!active && state === "default") {
        setVisible(true);
      }
    });
  }, []);

  const handleEnable = useCallback(async () => {
    setSubscribing(true);
    const ok = await subscribeToPush(clientId || undefined);
    setSubscribing(false);
    if (ok) {
      setVisible(false);
    }
  }, [clientId]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(LS_KEY, "1");
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: SP[5],
        right: SP[5],
        zIndex: 45,
        maxWidth: 380,
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.lg,
        padding: SP[5],
        boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
        transition: TRANSITION.normal,
        display: "flex",
        flexDirection: "column" as const,
        gap: SP[3],
      }}
      role="alert"
      aria-live="polite"
    >
      <div style={{ display: "flex", alignItems: "center", gap: SP[2] }}>
        <span style={{ fontSize: "1.25rem" }} aria-hidden="true">
          🔔
        </span>
        <strong style={{ color: T.text, fontSize: "0.875rem" }}>Ativar Notificações</strong>
      </div>
      <p style={{ color: T.sub, fontSize: "0.8125rem", margin: 0, lineHeight: 1.5 }}>
        Receba alertas sobre relatórios, rebalanceamento e atualizações do seu portfólio.
      </p>
      <div style={{ display: "flex", gap: SP[2], justifyContent: "flex-end" }}>
        <button
          onClick={handleDismiss}
          style={{
            background: "transparent",
            border: "none",
            color: T.muted,
            fontSize: "0.75rem",
            padding: `${SP[1]} ${SP[3]}`,
            cursor: "pointer",
            borderRadius: RADIUS.sm,
          }}
        >
          Agora não
        </button>
        <button
          onClick={handleEnable}
          disabled={subscribing}
          style={{
            background: T.accent,
            color: T.accentText,
            border: "none",
            fontSize: "0.75rem",
            fontWeight: 600,
            padding: `${SP[2]} ${SP[4]}`,
            borderRadius: RADIUS.sm,
            cursor: subscribing ? "wait" : "pointer",
            opacity: subscribing ? 0.7 : 1,
          }}
        >
          {subscribing ? "Ativando..." : "Ativar"}
        </button>
      </div>
    </div>
  );
}
